import React from 'react';
import { Select, DatePicker, Space } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

export type DateFilterValue = {
  preset: string;
  from?: string;
  to?: string;
};

interface DateFilterProps {
  value: DateFilterValue;
  onChange: (value: DateFilterValue) => void;
}

const presetToRange = (preset: string): { from: string; to: string } | null => {
  const now = dayjs();
  switch (preset) {
    case 'today': {
      const start = now.startOf('day');
      return { from: start.toISOString(), to: start.endOf('day').toISOString() };
    }
    case 'yesterday': {
      const day = now.subtract(1, 'day').startOf('day');
      return { from: day.toISOString(), to: day.endOf('day').toISOString() };
    }
    case 'currentWeek': {
      const start = now.startOf('week');
      return { from: start.toISOString(), to: start.endOf('week').toISOString() };
    }
    case 'previousWeek': {
      const start = now.subtract(1, 'week').startOf('week');
      return { from: start.toISOString(), to: start.endOf('week').toISOString() };
    }
    case 'thisMonth': {
      const start = now.startOf('month');
      return { from: start.toISOString(), to: start.endOf('month').toISOString() };
    }
    case 'lastMonth': {
      const start = now.subtract(1, 'month').startOf('month');
      return { from: start.toISOString(), to: start.endOf('month').toISOString() };
    }
    default:
      return null;
  }
};

const DateFilter: React.FC<DateFilterProps> = ({ value, onChange }) => {
  const handlePresetChange = (preset: string) => {
    if (!preset) {
      onChange({ preset: '', from: undefined, to: undefined });
      return;
    }
    if (preset === 'custom') {
      onChange({ preset: 'custom', from: undefined, to: undefined });
      return;
    }
    const range = presetToRange(preset);
    if (range) {
      onChange({ preset, ...range });
    }
  };

  const handleRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (!dates || !dates[0] || !dates[1]) {
      onChange({ preset: 'custom', from: undefined, to: undefined });
      return;
    }
    onChange({
      preset: 'custom',
      from: dates[0].startOf('day').toISOString(),
      to: dates[1].endOf('day').toISOString(),
    });
  };

  const rangeValue: RangePickerProps['value'] =
    value.preset === 'custom' && value.from && value.to
      ? [dayjs(value.from), dayjs(value.to)]
      : null;

  return (
    <Space>
      <Select
        value={value.preset || ''}
        onChange={handlePresetChange}
        style={{ width: 180 }}
        placeholder="Date range"
      >
        <Option value="">Any time</Option>
        <Option value="today">Today</Option>
        <Option value="yesterday">Yesterday</Option>
        <Option value="currentWeek">Current Week</Option>
        <Option value="previousWeek">Previous Week</Option>
        <Option value="thisMonth">This Month</Option>
        <Option value="lastMonth">Last Month</Option>
        <Option value="custom">Custom Range</Option>
      </Select>
      {value.preset === 'custom' && (
        <RangePicker
          value={rangeValue}
          onChange={handleRangeChange}
          allowClear
        />
      )}
    </Space>
  );
};

export default DateFilter;

